import { useState } from 'react'
import { useHostUsage } from '@/api/queries'
import type { HostUsage } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Badge, ratioTone } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { compactNumber, megabytesToGb, percent } from '@/lib/format'
import { HostDetailsDialog } from './HostDetailsDialog'

const errorRatio = (h: HostUsage) => (h.numberOfRequests ? (h.numberOfErrors / h.numberOfRequests) * 100 : 0)

export function SiteUsageDialog({ onClose }: { onClose: () => void }) {
  const { data, isLoading, error } = useHostUsage()
  const [host, setHost] = useState<string | null>(null)

  const columns: Column<HostUsage>[] = [
    {
      key: 'host',
      header: 'Host',
      render: (h) => (
        <button type="button" className="u-linkButton" onClick={() => setHost(h.host)} title="Requests per minute">
          {h.host}
        </button>
      ),
    },
    { key: 'numberOfRequests', header: 'Requests', align: 'right', render: (h) => compactNumber(h.numberOfRequests) },
    { key: 'numberOfIps', header: 'IPs', align: 'right' },
    { key: 'numberOfSuccess', header: 'Success', align: 'right', render: (h) => compactNumber(h.numberOfSuccess) },
    { key: 'numberOfErrors', header: 'Errors', align: 'right', render: (h) => compactNumber(h.numberOfErrors) },
    {
      key: 'errorRatio',
      header: 'Error %',
      align: 'right',
      value: errorRatio,
      render: (h) => <Badge tone={ratioTone(errorRatio(h), { warnAbove: 5, dangerAbove: 20 })}>{percent(errorRatio(h), 1)}</Badge>,
    },
    { key: 'averageTimePerRequestMs', header: 'Avg ms', align: 'right', render: (h) => Math.round(h.averageTimePerRequestMs) },
    { key: 'sizeInMb', header: 'GB', align: 'right', render: (h) => megabytesToGb(h.sizeInMb) },
  ]

  return (
    <Modal title="Sites usage · today" onClose={onClose} size="xl">
      <DataTable
        columns={columns}
        rows={data}
        rowKey={(h) => h.host}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search a site"
        dense
        initialSort={{ key: 'numberOfRequests', direction: 'desc' }}
        copyText={(rows) => rows.map((r) => `${r.host}\t${r.numberOfRequests}\t${r.numberOfErrors}`).join('\n')}
      />
      {host && <HostDetailsDialog host={host} onClose={() => setHost(null)} />}
    </Modal>
  )
}
